import { Check, Clock, ExternalLink, Newspaper, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { BlogPostStatusBadge, FilterChips, Pagination } from '../components/ui/data';
import { useToast } from '../components/ui/overlays';
import {
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui/primitives';
import {
  useBlogPostStatusCounts,
  useBlogPosts,
  useBulkApproveBlogPosts,
  type PostFilters,
} from '../hooks/queries';
import { formatRelative, truncate } from '../lib/utils';
import { BlogPostDrawer } from './content/BlogPostDrawer';
import { QueueContentModal } from './content/QueueContentModal';

const STATUS_CHIPS = [
  { value: 'DRAFT', label: 'Drafts' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'PUBLISHING', label: 'Publishing' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const BlogPostsPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [baseFilters, setFilters] = useState<PostFilters>({ page: 1, pageSize: 25 });
  const filters = { ...baseFilters, status: searchParams.get('status') || undefined };
  const [openId, setOpenId] = useState<string | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const bulkApprove = useBulkApproveBlogPosts(projectId);
  const toast = useToast();

  const { data, isLoading, isPlaceholderData, error } = useBlogPosts(projectId, filters);
  const { data: counts } = useBlogPostStatusCounts(projectId);

  const posts = data?.items ?? [];
  const eligible = posts.filter((post) => post.status === 'DRAFT' || post.status === 'FAILED');
  const selectedOnPage = eligible.filter((post) => selected.has(post.id)).length;
  const allSelected = eligible.length > 0 && selectedOnPage === eligible.length;
  const selectionDisabled = isPlaceholderData || bulkApprove.isPending;
  const toggle = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else if (next.size < 500) next.add(id);
    return next;
  });

  useEffect(() => setFilters((current) => ({ ...current, page: 1 })), [searchParams]);
  useEffect(() => { setSelected(new Set()); setOpenId(null); }, [projectId, filters.status]);

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Posts"
        description="Drafts written by content agents, what is scheduled, and everything already published."
        actions={<Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setQueueOpen(true)}>Create posts</Button>}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <FilterChips
            options={STATUS_CHIPS}
            value={filters.status}
            onChange={(status) => {
              const params = new URLSearchParams(searchParams);
              status ? params.set('status', status) : params.delete('status');
              setSearchParams(params, { replace: true });
              setFilters((current) => ({ ...current, page: 1 }));
            }}
            counts={counts}
          />
        </div>

        {selected.size > 0 ? <div className="flex flex-wrap items-center gap-3 border-b border-line bg-brand/5 px-4 py-3" role="region" aria-label="Bulk post actions">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-ink">{selected.size} {selected.size === 1 ? 'post' : 'posts'} selected{selected.size > selectedOnPage ? ' across pages' : ''}</p>
            <p className="mt-0.5 text-xs text-muted">Approval schedules publishing within each channel’s limits and publishing window. Select up to 500 posts.</p>
          </div>
          <Button variant="ghost" size="sm" disabled={bulkApprove.isPending} onClick={() => setSelected(new Set())}>Clear selection</Button>
          <Button variant="primary" size="sm" icon={<Check className="h-4 w-4" />} loading={bulkApprove.isPending} disabled={isPlaceholderData} onClick={async () => {
            try {
              const result = await bulkApprove.mutateAsync([...selected]);
              setSelected(new Set());
              toast.success(`${result.approved} ${result.approved === 1 ? 'post' : 'posts'} approved for publishing.${result.skipped ? ` ${result.skipped} skipped because their status changed.` : ''}`);
            } catch (caught) { toast.error(caught); }
          }}>Approve {selected.size} {selected.size === 1 ? 'post' : 'posts'}</Button>
        </div> : eligible.length > 0 ? <p className="border-b border-line px-4 py-2 text-xs text-muted">Select drafts or failed posts to approve them together.</p> : null}

        {error ? <div className="p-5"><ErrorBlock error={error} /></div> : posts.length === 0 ? (
          <EmptyState
            icon={<Newspaper className="h-5 w-5" />}
            title={filters.status ? 'No posts with this status' : 'No posts yet'}
            description={filters.status ? 'Choose another status or view all posts.' : 'Create posts from your topics and let a content agent prepare the drafts for review.'}
            action={filters.status ? <Button onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete('status');
              setSearchParams(params, { replace: true });
            }}>View all posts</Button> : <Button variant="primary" onClick={() => setQueueOpen(true)}>Create posts</Button>}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th className="w-10"><input type="checkbox" aria-label="Select all eligible posts on this page" className="h-4 w-4 rounded accent-brand" checked={allSelected} ref={(input) => { if (input) input.indeterminate = selectedOnPage > 0 && !allSelected; }} disabled={selectionDisabled || !eligible.length || (!allSelected && selected.size + eligible.length - selectedOnPage > 500)} onChange={() => setSelected((current) => {
                    const next = new Set(current);
                    eligible.forEach((post) => allSelected ? next.delete(post.id) : next.add(post.id));
                    return next;
                  })} /></Th>
                  <Th>Title</Th>
                  <Th>Topic</Th>
                  <Th>Status</Th>
                  <Th className="text-right">SEO</Th>
                  <Th>When</Th>
                  <Th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <Tr key={post.id} className={selected.has(post.id) ? 'bg-brand/5' : undefined}>
                    <Td><input type="checkbox" aria-label={`Select ${post.title}`} className="h-4 w-4 rounded accent-brand" checked={selected.has(post.id)} disabled={selectionDisabled || !['DRAFT', 'FAILED'].includes(post.status) || (!selected.has(post.id) && selected.size >= 500)} onChange={() => toggle(post.id)} /></Td>
                    <Td className="cursor-pointer" onClick={() => setOpenId(post.id)}>
                      <button type="button" onClick={() => setOpenId(post.id)} className="block max-w-[24rem] truncate rounded text-left font-medium text-ink hover:text-brand hover:underline">
                        {truncate(post.title, 72)}
                      </button>
                      {post.excerpt ? (
                        <div className="max-w-[24rem] truncate text-xs text-faint">
                          {truncate(post.excerpt, 90)}
                        </div>
                      ) : null}
                    </Td>
                    <Td className="cursor-pointer text-muted" onClick={() => setOpenId(post.id)}>
                      {post.topic ? truncate(post.topic.title, 40) : <span className="text-faint">—</span>}
                    </Td>
                    <Td>
                      <BlogPostStatusBadge status={post.status} />
                    </Td>
                    <Td className="text-right tabular-nums text-muted">
                      {post.seoScore ?? <span className="text-faint">—</span>}
                    </Td>
                    <Td className="whitespace-nowrap text-faint">
                      {post.status === 'SCHEDULED' && post.scheduledAt ? (
                        <span className="flex items-center gap-1 text-warning">
                          <Clock className="h-3 w-3" />
                          {formatRelative(post.scheduledAt)}
                        </span>
                      ) : (
                        formatRelative(post.publishedAt ?? post.createdAt)
                      )}
                    </Td>
                    <Td>
                      <div className="flex justify-end gap-1">
                        {post.externalUrl ? (
                          <a
                            href={post.externalUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[13px] text-brand hover:underline"
                          >
                            View <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setOpenId(post.id)}>
                            Open
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={data?.page ?? 1}
              totalPages={data?.totalPages ?? 1}
              total={data?.total ?? 0}
              pageSize={data?.pageSize ?? 25}
              onPage={(page) => setFilters((current) => ({ ...current, page }))}
            />
          </>
        )}
      </Card>

      <BlogPostDrawer postId={openId} projectId={projectId} onClose={() => setOpenId(null)} />
      <QueueContentModal key={projectId} open={queueOpen} onClose={() => setQueueOpen(false)} projectId={projectId} topicIds={[]} />
    </Page>
  );
};
